import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Cake, Sparkles, PartyPopper } from 'lucide-react';
import api from '@/lib/api';

const DAYS_HEADER = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// Deterministic pastel colour from a user id (so each employee gets a stable colour)
function colorFor(userId) {
  let h = 0;
  const s = String(userId || '');
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) & 0xffffff;
  const hue = h % 360;
  return `hsl(${hue}, 70%, 55%)`;
}

function daysInMonth(year, month /* 1-12 */) {
  return new Date(year, month, 0).getDate();
}
function firstWeekday(year, month) {
  // returns 0..6 (Sun..Sat)
  return new Date(year, month - 1, 1).getDay();
}
function ymd(year, month, day) {
  return `${year}-${String(month).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
}

/** Given a leave record with start/end dates, is `dayIso` inside its range? */
function leaveCoversDay(leave, dayIso) {
  const s = leave.start_date || '';
  const e = leave.end_date || s;
  return dayIso >= s && dayIso <= e;
}

export default function LeaveCalendar() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1..12
  const [department, setDepartment] = useState('all');
  const [leaveType, setLeaveType] = useState('all');
  const [data, setData] = useState({ leaves: [], holidays: [], birthdays: [] });
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null); // ISO date string when clicked

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/departments');
        setDepartments(r.data || []);
      } catch { /* ignore */ }
    })();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { year, month };
        if (department !== 'all') params.department = department;
        const r = await api.get('/leave-calendar', { params });
        if (!cancelled) setData(r.data || { leaves: [], holidays: [], birthdays: [] });
      } catch (err) {
        toast.error('Failed to load leave calendar');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year, month, department]);

  const leaveTypes = useMemo(() => {
    const set = new Set();
    (data.leaves || []).forEach(l => l.leave_type && set.add(l.leave_type));
    return Array.from(set);
  }, [data.leaves]);

  const filteredLeaves = useMemo(() => {
    if (leaveType === 'all') return data.leaves || [];
    return (data.leaves || []).filter(l => l.leave_type === leaveType);
  }, [data.leaves, leaveType]);

  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); } else { setMonth(m => m - 1); }
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); } else { setMonth(m => m + 1); }
  };
  const goToday = () => { setYear(today.getFullYear()); setMonth(today.getMonth() + 1); };

  // Build grid cells
  const totalDays = daysInMonth(year, month);
  const startWeekday = firstWeekday(year, month);
  const totalCells = Math.ceil((startWeekday + totalDays) / 7) * 7;
  const cells = Array.from({ length: totalCells }, (_, i) => {
    const dayNum = i - startWeekday + 1;
    if (dayNum < 1 || dayNum > totalDays) return null;
    return dayNum;
  });

  const holidaysByDate = useMemo(() => {
    const map = {};
    (data.holidays || []).forEach(h => { map[h.date] = (map[h.date] || []).concat(h); });
    return map;
  }, [data.holidays]);

  const birthdaysByDate = useMemo(() => {
    const map = {};
    (data.birthdays || []).forEach(b => { map[b.date] = (map[b.date] || []).concat(b); });
    return map;
  }, [data.birthdays]);

  const leavesByDate = useMemo(() => {
    const map = {};
    for (let d = 1; d <= totalDays; d++) {
      const iso = ymd(year, month, d);
      map[iso] = filteredLeaves.filter(l => leaveCoversDay(l, iso));
    }
    return map;
  }, [filteredLeaves, year, month, totalDays]);

  const todayIso = ymd(today.getFullYear(), today.getMonth() + 1, today.getDate());

  const selectedItems = useMemo(() => {
    if (!selectedDay) return { leaves: [], holidays: [], birthdays: [] };
    return {
      leaves: leavesByDate[selectedDay] || [],
      holidays: holidaysByDate[selectedDay] || [],
      birthdays: birthdaysByDate[selectedDay] || [],
    };
  }, [selectedDay, leavesByDate, holidaysByDate, birthdaysByDate]);

  return (
    <div className="p-8 lg:p-12 space-y-6">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-semibold">Leave Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">Team leaves, holidays, and birthdays at a glance.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" size="icon" onClick={prevMonth} data-testid="cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
          <Button variant="outline" onClick={goToday} data-testid="cal-today">Today</Button>
          <Button variant="outline" size="icon" onClick={nextMonth} data-testid="cal-next"><ChevronRight className="w-4 h-4" /></Button>
          <div className="text-lg font-semibold min-w-[180px] text-center">{MONTHS[month - 1]} {year}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All departments" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={leaveType} onValueChange={setLeaveType}>
          <SelectTrigger className="w-48"><SelectValue placeholder="All leave types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All leave types</SelectItem>
            {leaveTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: '#16a34a' }} /> Holiday</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded" style={{ background: 'hsl(220, 70%, 55%)' }} /> Approved leave</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded border border-dashed border-current" /> Pending leave</span>
          <span className="flex items-center gap-1"><Cake className="w-3 h-3" /> Birthday</span>
        </div>
      </div>

      {loading ? (
        <Card><CardContent className="py-16 text-center text-muted-foreground">Loading calendar…</CardContent></Card>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[1fr,340px]">
          <Card>
            <CardContent className="p-0">
              <div className="grid grid-cols-7 border-b">
                {DAYS_HEADER.map(d => (
                  <div key={d} className="p-2 text-center text-xs font-semibold text-muted-foreground">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {cells.map((day, idx) => {
                  if (day === null) return <div key={idx} className="aspect-square border-r border-b bg-muted/20" />;
                  const iso = ymd(year, month, day);
                  const isToday = iso === todayIso;
                  const isSelected = selectedDay === iso;
                  const dayLeaves = leavesByDate[iso] || [];
                  const dayHolidays = holidaysByDate[iso] || [];
                  const dayBirthdays = birthdaysByDate[iso] || [];
                  const isHoliday = dayHolidays.length > 0;
                  const weekday = new Date(year, month - 1, day).getDay();
                  const isWeekend = weekday === 0 || weekday === 6;

                  return (
                    <button
                      key={idx}
                      onClick={() => setSelectedDay(iso)}
                      className={`
                        aspect-square border-r border-b p-1.5 text-left transition
                        hover:bg-muted/50 relative overflow-hidden
                        ${isHoliday ? 'bg-green-50' : ''}
                        ${isWeekend && !isHoliday ? 'bg-muted/10' : ''}
                        ${isSelected ? 'ring-2 ring-inset ring-[#002FA7]' : ''}
                      `}
                      data-testid={`cal-day-${iso}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-semibold ${isToday ? 'inline-flex w-6 h-6 items-center justify-center bg-[#002FA7] text-white rounded-full' : ''}`}>
                          {day}
                        </span>
                        <div className="flex items-center gap-0.5">
                          {dayBirthdays.length > 0 && <Cake className="w-3 h-3 text-pink-500" />}
                          {isHoliday && <PartyPopper className="w-3 h-3 text-green-700" />}
                        </div>
                      </div>
                      {isHoliday && (
                        <div className="mt-1 text-[10px] font-medium text-green-800 truncate">
                          {dayHolidays[0].name}
                        </div>
                      )}
                      <div className="mt-1 space-y-0.5">
                        {dayLeaves.slice(0, 3).map(l => (
                          <div
                            key={l.id}
                            title={`${l.user_name} · ${l.leave_type} · ${l.status}`}
                            className={`text-[10px] px-1 py-0.5 rounded truncate ${l.status === 'pending' ? 'border border-dashed' : 'text-white'}`}
                            style={l.status === 'pending'
                              ? { borderColor: colorFor(l.user_id), color: colorFor(l.user_id) }
                              : { background: colorFor(l.user_id) }}
                          >
                            {l.user_name}
                          </div>
                        ))}
                        {dayLeaves.length > 3 && (
                          <div className="text-[10px] text-muted-foreground">+{dayLeaves.length - 3} more</div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {selectedDay ? new Date(selectedDay).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' }) : 'Select a day'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!selectedDay ? (
                  <div className="text-sm text-muted-foreground py-4">Tap any date on the calendar to see who is off, upcoming holidays, and birthdays.</div>
                ) : (
                  <div className="space-y-3">
                    {selectedItems.holidays.map(h => (
                      <div key={h.id} className="flex items-start gap-2 p-2 rounded bg-green-50 border border-green-200">
                        <PartyPopper className="w-4 h-4 text-green-700 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-sm">{h.name}</div>
                          {h.description && <div className="text-xs text-muted-foreground">{h.description}</div>}
                          <Badge variant="outline" className="text-[10px] mt-1">{h.type}</Badge>
                        </div>
                      </div>
                    ))}
                    {selectedItems.birthdays.map(b => (
                      <div key={b.user_id} className="flex items-center gap-2 p-2 rounded bg-pink-50 border border-pink-200">
                        <Cake className="w-4 h-4 text-pink-500" />
                        <div className="flex-1">
                          <div className="text-sm">🎉 <b>{b.user_name}</b>&apos;s birthday</div>
                          {b.department && <div className="text-xs text-muted-foreground">{b.department}</div>}
                        </div>
                      </div>
                    ))}
                    {selectedItems.leaves.length === 0 && selectedItems.holidays.length === 0 && selectedItems.birthdays.length === 0 && (
                      <div className="text-sm text-muted-foreground py-2">Nothing scheduled for this day.</div>
                    )}
                    {selectedItems.leaves.map(l => (
                      <div key={l.id} className="flex items-center gap-2 p-2 border rounded">
                        <Avatar className="w-7 h-7"><AvatarImage src={l.user_avatar} /><AvatarFallback>{(l.user_name || '?')[0]}</AvatarFallback></Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{l.user_name}</div>
                          <div className="text-xs text-muted-foreground">
                            {l.leave_type} · <Badge variant={l.status === 'approved' ? 'default' : 'outline'} className="text-[10px]">{l.status}</Badge>
                            {l.user_department && <> · {l.user_department}</>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {(data.holidays || []).length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-1"><Sparkles className="w-4 h-4" /> Upcoming Holidays</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {(data.holidays || []).slice(0, 6).map(h => (
                      <div key={h.id} className="flex items-center justify-between text-sm">
                        <div className="font-medium">{h.name}</div>
                        <div className="text-xs text-muted-foreground">{new Date(h.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}</div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
